/**
 * Better Auth TypeScript Types
 * 
 * Type definitions for Better Auth API responses and entities.
 * Used throughout the application for type-safe authentication.
 * 
 * @see Backend Feature 005 for API implementation
 * @see https://www.better-auth.com/docs for Better Auth documentation
 */

/**
 * User entity from Better Auth.
 * Represents an authenticated user with role and ban information.
 */
export interface BetterAuthUser {
  /** Unique user identifier (UUID) */
  id: string;
  /** User's email address */
  email: string;
  /** Display name (nullable for users who haven't set one) */
  name: string | null;
  /** Whether the email has been verified */
  emailVerified: boolean;
  /** Profile image URL (nullable) */
  image: string | null;
  /** User roles as comma-separated string (e.g., "user" or "admin,user") */
  role: string;
  /** Whether the user is currently banned */
  banned: boolean;
  /** Reason for the ban (null if not banned or no reason provided) */
  banReason: string | null;
  /** When the ban expires (null for permanent bans or not banned) */
  banExpires: string | null;
  /** ISO timestamp of account creation */
  createdAt: string;
  /** ISO timestamp of last update */
  updatedAt: string;
}

/**
 * Session entity from Better Auth.
 * Contains the session token and associated user data.
 */
export interface BetterAuthSession {
  /** Session token (used for Authorization header) */
  token: string;
  /** Session ID (for revocation purposes) */
  id: string;
  /** Associated user ID */
  userId: string;
  /** ISO timestamp when session expires */
  expiresAt: string;
  /** ISO timestamp of session creation */
  createdAt: string;
  /** User agent string from session creation */
  userAgent: string | null;
  /** IP address from session creation */
  ipAddress: string | null;
}

/**
 * Combined session response from GET /v1/auth/get-session.
 * Contains both session and user data for authenticated requests.
 */
export interface BetterAuthSessionResponse {
  /** Session data */
  session: BetterAuthSession;
  /** User data */
  user: BetterAuthUser;
}

/**
 * Request payload for email/password sign-in.
 * POST /v1/auth/sign-in/email
 */
export interface SignInEmailRequest {
  /** User's email address */
  email: string;
  /** User's password */
  password: string;
  /** Whether to remember the session (extends expiry) */
  rememberMe?: boolean;
}

/**
 * Response from email/password sign-in.
 * POST /v1/auth/sign-in/email
 * 
 * Note: Better Auth returns token at root level, not nested in session object.
 */
export interface SignInEmailResponse {
  /** Session token for authentication */
  token: string;
  /** Whether a redirect is needed (false for API calls) */
  redirect: boolean;
  /** Authenticated user data */
  user: BetterAuthUser;
}

/**
 * Request payload for sign-out.
 * POST /v1/auth/sign-out
 */
export interface SignOutRequest {
  /** Session token to invalidate (optional - uses header token if not provided) */
  token?: string;
}

/**
 * Response from sign-out.
 * POST /v1/auth/sign-out
 */
export interface SignOutResponse {
  /** Whether sign-out was successful */
  success: boolean;
}

/**
 * OAuth social sign-in providers supported by the backend.
 */
export type OAuthProvider = 'google';

/**
 * OAuth redirect URL parameters.
 * GET /v1/auth/sign-in/social?provider=<provider>
 */
export interface OAuthSignInParams {
  /** OAuth provider to use */
  provider: OAuthProvider;
  /** Optional callback URL after OAuth completes */
  callbackURL?: string;
}

/**
 * OAuth callback query parameters.
 * GET /v1/auth/callback/<provider>
 */
export interface OAuthCallbackParams {
  /** OAuth authorization code */
  code?: string;
  /** OAuth state parameter */
  state?: string;
  /** Error code if OAuth failed */
  error?: string;
  /** Error description */
  error_description?: string;
}

/**
 * Error response from Better Auth API.
 */
export interface BetterAuthError {
  /** Error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
  /** HTTP status code */
  status: number;
  /** Additional error details (optional) */
  details?: Record<string, unknown>;
}

/**
 * Helper type to check if a user has admin role.
 * Parses the comma-separated role string.
 */
export function isAdminUser(user: BetterAuthUser): boolean {
  if (!user.role) return false;
  const roles = user.role.split(',').map(r => r.trim().toLowerCase());
  return roles.includes('admin');
}

/**
 * Helper type to parse user roles from comma-separated string.
 */
export function parseUserRoles(role: string | null): string[] {
  if (!role) return [];
  return role.split(',').map(r => r.trim()).filter(Boolean);
}

/**
 * Check if a session is expired.
 */
export function isSessionExpired(session: BetterAuthSession): boolean {
  return new Date(session.expiresAt) <= new Date();
}

/**
 * Check if a session needs refresh (less than 1 day remaining).
 */
export function sessionNeedsRefresh(session: BetterAuthSession): boolean {
  const expiresAt = new Date(session.expiresAt);
  const oneDayFromNow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return expiresAt <= oneDayFromNow;
}
